import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertCircle, ChevronDown, ChevronUp, History, Loader2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const _fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleDateString();
};

const _durationDays = (from, to) => {
  if (!from || !to) return null;
  const f = new Date(from);
  const t = new Date(to);
  if (isNaN(f) || isNaN(t)) return null;
  return Math.max(1, Math.round((t.getTime() - f.getTime()) / 86400000));
};

/**
 * Extension Reassignment Audit — collapsible timeline card.
 *
 * Yabbit reuses extensions when reps leave (ext 891 was Daniel Perkins,
 * now Sam Haines). This panel reconstructs each handover from historical
 * `calls.agent_name` and surfaces per-owner stats (volume, avg score)
 * so managers can spot performance drops that coincide with a rep
 * change.
 *
 * Server call: GET /api/manager/extensions/reassignments
 * Only extensions with 2+ distinct owners appear by default.
 */
const ExtensionReassignmentsPanel = ({ domainId }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [openExt, setOpenExt] = useState(null); // extension currently expanded

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = new URLSearchParams();
      if (domainId) p.set('domain_id', domainId);
      p.set('days', '365');
      p.set('only_reassigned', 'true');
      const r = await axios.get(`${API}/manager/extensions/reassignments?${p.toString()}`);
      setRows(r.data?.rows || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load extension audit.');
    } finally {
      setLoading(false);
    }
  }, [domainId]);

  useEffect(() => {
    if (expanded && rows.length === 0 && !loading) load();
  }, [expanded, load, rows.length, loading]);

  // Group cohort-rows by extension for the timeline UI.
  const grouped = useMemo(() => {
    const map = {};
    for (const r of rows) {
      (map[r.extension] = map[r.extension] || []).push(r);
    }
    return map;
  }, [rows]);

  const extensionKeys = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="ext-audit-panel">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 rounded-t-xl"
        data-testid="ext-audit-panel-toggle"
      >
        <History className="w-4 h-4 text-indigo-600" />
        <div className="text-left">
          <p className="text-sm font-bold text-slate-900">Extension Change Audit</p>
          <p className="text-[11px] text-slate-500">
            Handover timelines for reassigned extensions — spot performance drops after a rep change
          </p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {expanded && extensionKeys.length > 0 && (
            <span className="text-[11px] font-semibold text-slate-600 tabular-nums">
              {extensionKeys.length} extension{extensionKeys.length === 1 ? '' : 's'} reassigned
            </span>
          )}
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-slate-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-500" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-200">
          <div className="flex items-center justify-end pt-2 pb-3">
            <button
              onClick={load}
              className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="ext-audit-refresh"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading audit…
            </div>
          )}
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          {!loading && !error && extensionKeys.length === 0 && (
            <p className="text-sm text-slate-500 italic py-6 text-center">
              No extension reassignments detected in the last 365 days.
            </p>
          )}

          <div className="space-y-2">
            {extensionKeys.map((ext) => {
              const cohort = grouped[ext];
              const isOpen = openExt === ext;
              const owners = cohort.map((c) => c.agent_name);
              // Detect a drop from the previous owner to the current one.
              let drop = null;
              if (cohort.length >= 2) {
                const prev = cohort[cohort.length - 2].avg_score;
                const curr = cohort[cohort.length - 1].avg_score;
                if (prev != null && curr != null) {
                  drop = Math.round((curr - prev) * 10) / 10;
                }
              }
              return (
                <div
                  key={ext}
                  className="rounded-lg border border-slate-200 bg-slate-50/50"
                  data-testid={`ext-audit-row-${ext}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenExt(isOpen ? null : ext)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-100 rounded-lg"
                  >
                    <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 tabular-nums">
                      ext {ext}
                    </span>
                    <span className="text-sm text-slate-700 truncate">
                      {owners.join(' → ')}
                    </span>
                    {drop != null && drop <= -5 && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700"
                        title="Avg score dropped after this handover"
                      >
                        <AlertCircle className="w-2.5 h-2.5" />
                        {drop} pts
                      </span>
                    )}
                    <span className="ml-auto text-[11px] text-slate-500">
                      {cohort.length} owner{cohort.length === 1 ? '' : 's'}
                    </span>
                    {isOpen ? (
                      <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="px-3 pb-3">
                      <table className="w-full text-xs">
                        <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="py-1 pr-2 font-semibold">Owner</th>
                            <th className="py-1 pr-2 font-semibold">First call</th>
                            <th className="py-1 pr-2 font-semibold">Last call</th>
                            <th className="py-1 pr-2 font-semibold text-right">Days</th>
                            <th className="py-1 pr-2 font-semibold text-right">Calls</th>
                            <th className="py-1 pr-2 font-semibold text-right">Avg</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cohort.map((c, idx) => {
                            const days = _durationDays(c.first_call_date, c.last_call_date);
                            return (
                              <tr
                                key={`${ext}-${c.agent_name}-${idx}`}
                                className={idx === cohort.length - 1 ? 'font-semibold text-slate-900' : 'text-slate-700'}
                              >
                                <td className="py-1 pr-2">
                                  {c.agent_name}
                                  {idx === cohort.length - 1 && (
                                    <span className="ml-1 text-[9px] uppercase tracking-wider text-emerald-700">current</span>
                                  )}
                                </td>
                                <td className="py-1 pr-2 tabular-nums">{_fmtDate(c.first_call_date)}</td>
                                <td className="py-1 pr-2 tabular-nums">{_fmtDate(c.last_call_date)}</td>
                                <td className="py-1 pr-2 tabular-nums text-right">{days ?? '—'}</td>
                                <td className="py-1 pr-2 tabular-nums text-right">{c.calls}</td>
                                <td className="py-1 pr-2 tabular-nums text-right">
                                  {c.avg_score != null ? c.avg_score : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtensionReassignmentsPanel;
